import { Component, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-screening-criteria-bar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <!-- Header -->
      <button
        type="button"
        (click)="isExpanded.set(!isExpanded())"
        class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <!-- Filter icon -->
        <svg
          class="w-4 h-4 text-slate-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        <span class="text-xs font-bold uppercase tracking-wider text-slate-600">
          Screening Criteria
        </span>
        <!-- Count badges (always visible) -->
        @if (inclusionCount >= 0) {
          <span
            class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 leading-none"
          >
            {{ inclusionCount }}
          </span>
        }
        @if (exclusionCount >= 0) {
          <span
            class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 leading-none"
          >
            {{ exclusionCount }}
          </span>
        }
        @if (inclusionCount === 0 && exclusionCount === 0) {
          <span class="text-[10px] text-slate-400 italic">empty</span>
        }
        <div class="flex-1"></div>
        <!-- Chevron -->
        <svg
          class="w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0"
          [class.rotate-180]="isExpanded()"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <!-- Expandable content -->
      <div
        [class.max-h-0]="!isExpanded()"
        [class.max-h-96]="isExpanded()"
        [class.overflow-y-auto]="isExpanded()"
        class="overflow-hidden transition-all duration-300 ease-in-out border-t border-slate-100"
      >
        <div class="px-4 py-3 space-y-3">
          <!-- Inclusion -->
          <div class="border-l-[3px] border-emerald-400 pl-3">
            <div class="flex items-center gap-1.5 mb-2">
              <svg
                class="w-4 h-4 text-emerald-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-xs font-semibold text-emerald-800">Inclusion Criteria</span>
              <span class="text-[10px] text-emerald-800">({{ inclusionCount }})</span>
            </div>
            @if (parseCriteriaList(inclusionCriteria); as items) {
              @if (items.length > 0) {
                <div class="flex flex-wrap gap-1.5">
                  @for (item of items; track $index) {
                    <span
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full bg-emerald-100 text-slate-900 ring-1 ring-emerald-500/20"
                    >
                      <span
                        class="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-[9px] font-bold text-white leading-none flex-shrink-0"
                      >
                        {{ $index + 1 }}
                      </span>
                      {{ item }}
                    </span>
                  }
                </div>
              } @else {
                <p class="text-xs text-slate-400 italic">No inclusion criteria defined.</p>
              }
            }
          </div>

          <!-- Exclusion -->
          <div class="border-l-[3px] border-amber-400 pl-3">
            <div class="flex items-center gap-1.5 mb-2">
              <svg
                class="w-4 h-4 text-amber-500 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-xs font-semibold text-amber-800">Exclusion Criteria</span>
              <span class="text-[10px] text-amber-800">({{ exclusionCount }})</span>
            </div>
            @if (parseCriteriaList(exclusionCriteria); as items) {
              @if (items.length > 0) {
                <div class="flex flex-wrap gap-1.5">
                  @for (item of items; track $index) {
                    <span
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full bg-amber-100 text-slate-900 ring-1 ring-amber-500/20"
                    >
                      <span
                        class="flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-[9px] font-bold text-white leading-none flex-shrink-0"
                      >
                        {{ $index + 1 }}
                      </span>
                      {{ item }}
                    </span>
                  }
                </div>
              } @else {
                <p class="text-xs text-slate-400 italic">No exclusion criteria defined.</p>
              }
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ScreeningCriteriaBarComponent {
  @Input() inclusionCriteria: string | undefined | null = null;
  @Input() exclusionCriteria: string | undefined | null = null;

  readonly isExpanded = signal(true);

  /** Number of inclusion criteria — shown as badge in the collapsed header. */
  get inclusionCount(): number {
    return this.parseCriteriaList(this.inclusionCriteria).length;
  }

  /** Number of exclusion criteria — shown as badge in the collapsed header. */
  get exclusionCount(): number {
    return this.parseCriteriaList(this.exclusionCriteria).length;
  }

  parseCriteriaList(val: string | undefined | null): string[] {
    if (!val) return [];
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return val
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
  }
}
